import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";

import { Button } from "common/components/ui/Button";

import type { ComponentProps } from "react";

interface PaginationProps extends ComponentProps<"div"> {
  pageSize: number;
  itemCount: number;
  setPage: (page: number) => void;
  selectedIndex: number;
  isLastPageHidden: boolean;
  size?: "sm" | "md";
}

export default function Pagination({
  className,
  pageSize,
  itemCount,
  setPage,
  selectedIndex,
  isLastPageHidden,
  size = "md",
  ...props
}: PaginationProps) {
  const pagesCount = Math.ceil(itemCount / pageSize);

  if (pagesCount <= 1) {
    return null;
  }

  const isEllipsisPagination = pagesCount > 3;
  const pageNumbers = [];
  let isPageNumberOutOfRange = false;

  for (let i = 0; i < pagesCount; i += 1) {
    const isFirstPage = i === 0;
    const isLastPage = i === pagesCount - 1;
    const isCurrentPageWithinOnePageNumber = Math.abs(i - selectedIndex) <= 1;

    if (
      isFirstPage ||
      (isLastPage && !isLastPageHidden) ||
      isCurrentPageWithinOnePageNumber
    ) {
      isPageNumberOutOfRange = false;
      const isActive = i === selectedIndex;
      pageNumbers.push(
        <Button
          variant={isActive ? "default-black" : "ghost-black"}
          className="select-none rounded-2xl p-0 transition-none"
          key={i}
          intent="iconOnly"
          size={size}
          onClick={() => setPage(i)}
        >
          {i + 1}
        </Button>,
      );
    } else if (!isPageNumberOutOfRange && !isLastPageHidden) {
      isPageNumberOutOfRange = true;
      pageNumbers.push(
        <span key={`ellipsis-${i}`} className="select-none text-black-500">
          ...
        </span>,
      );
    }
  }

  return (
    <div
      className={clsx(
        "mx-auto flex items-center justify-center gap-x-1",
        isEllipsisPagination && (size === "sm" ? "w-72" : "w-96"),
      )}
      {...props}
    >
      <Button
        aria-label="Go to previous page"
        intent="iconOnly"
        className={clsx(
          "rounded-2xl",
          isEllipsisPagination ? "mr-auto" : "mr-6",
        )}
        variant="outline-black"
        onClick={() => setPage(selectedIndex - 1)}
        disabled={selectedIndex === 0}
        size={size}
      >
        <CaretLeft />
      </Button>
      {pageNumbers}
      <Button
        aria-label="Go to next page"
        intent="iconOnly"
        className={clsx(
          "rounded-2xl",
          isEllipsisPagination ? "ml-auto" : "ml-6",
        )}
        variant="outline-black"
        disabled={selectedIndex === pagesCount - 1}
        onClick={() => setPage(selectedIndex + 1)}
        size={size}
      >
        <CaretRight />
      </Button>
    </div>
  );
}
